import { Button } from 'components/Button'
import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { Icon } from 'components/Icon'
import { commonStrings, organizationManagementStrings, headerStrings } from 'constants/strings'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import type { TOrganization } from 'types'
import { formatDateTime } from 'utils'
import { buildEntryUrl } from 'utils/buildEntryUrl'

type OrganizationsTableProps = {
  organizations: TOrganization[]
  loading: boolean
  emptyMessage?: string
  onEdit?: (organizationId: number) => void
}

export function OrganizationsTable({
  organizations,
  loading,
  emptyMessage = organizationManagementStrings.states.empty,
  onEdit,
}: OrganizationsTableProps) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = <DataTableMessageRow colSpan={7} message={commonStrings.loading} />
  } else if (organizations.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={7}
        message={emptyMessage}
      />
    )
  } else {
    body = organizations.map((organization) => (
      <tr key={organization.id} className="border-t border-border/60 bg-white">
        <td className="px-2 py-3 text-left sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.05)]">
          {onEdit ? (
            <Button type="button" size="sm" onClick={() => onEdit(organization.id)}>
              {commonStrings.actions.edit}
            </Button>
          ) : (
            <span className="text-[11px] text-muted">-</span>
          )}
        </td>
        <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
          {organization.id}
        </td>
        <td className="whitespace-nowrap px-2 py-3 text-left">
          <CopyEntryUrl entryKey={organization.entryKey} />
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {organization.entryKey ?? '-'}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {organization.name}
        </td>
        <td className="max-w-[280px] px-2 py-3 text-[11px] text-ink sm:text-sm">
          {organizationManagementStrings.planLabels[organization.plan]}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {formatDateTime(organization.createdAt)}
        </td>
      </tr>
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[960px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight sticky left-0 z-10 bg-surface/70">
            {organizationManagementStrings.table.headers.actions}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {organizationManagementStrings.table.headers.id}
          </th>
          <th className="whitespace-nowrap px-2 py-3 text-left leading-tight">
            {headerStrings.copyEntryUrl}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {organizationManagementStrings.table.headers.entryKey}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {organizationManagementStrings.table.headers.name}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {organizationManagementStrings.table.headers.plan}
          </th>
          <th className="px-2 py-3 text-left leading-tight">
            {organizationManagementStrings.table.headers.createdAt}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

function CopyEntryUrl({ entryKey }: { entryKey?: string }) {
  const { status, copy } = useCopyToClipboard()

  if (!entryKey) return <span className="text-[11px] text-muted">-</span>

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => copy(buildEntryUrl(entryKey))}
        disabled={status !== 'idle'}
        aria-label={headerStrings.copyEntryUrl}
      >
        {status === 'success' ? (
          <>
            <Icon icon="check" className="mr-1 h-3 w-3 text-green-600" />
            {headerStrings.copyEntryUrlSuccess}
          </>
        ) : status === 'error' ? (
          headerStrings.copyEntryUrlError
        ) : (
          headerStrings.copyEntryUrl
        )}
      </Button>
      <span aria-live="polite" className="sr-only">
        {status === 'success' ? headerStrings.copyEntryUrlSuccess : status === 'error' ? headerStrings.copyEntryUrlError : ''}
      </span>
    </>
  )
}
